import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Sparkles, Loader2, X, Lock, Send } from "lucide-react";

const SUGGESTED_PROMPTS = [
  "How should I respond to their last message?",
  "Should I suggest meeting up? When?",
  "Help me ask a more interesting question.",
  "Is this conversation going well?",
];

export default function CoachPanel({ matchId, otherName, isQuietPlus, onClose }) {
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState([]); // [{q, a}]
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [history, busy]);

  const ask = async (q) => {
    const body = (q ?? question).trim();
    if (!body || busy) return;
    setHistory((h) => [...h, { q: body, a: null }]);
    setQuestion("");
    setBusy(true);
    try {
      const { data } = await api.post(`/ai/coach/${matchId}`, { question: body });
      setHistory((h) => {
        const next = [...h];
        next[next.length - 1] = { q: body, a: data.advice };
        return next;
      });
    } catch (err) {
      setHistory((h) => {
        const next = [...h];
        next[next.length - 1] = { q: body, a: "I lost my train of thought. Try again in a moment." };
        return next;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex justify-end" onClick={onClose}
         data-testid="coach-overlay">
      <motion.aside
        initial={{ x: 480, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.35, ease: "easeOut" }}
        className="w-full max-w-md h-full bg-[#FAF7F2] grain-bg border-l border-[#E5DFD3] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="coach-panel"
      >
        <header className="px-6 py-5 border-b border-[#E5DFD3] flex items-center justify-between bg-white/60">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#913831]">Quiet+ &middot; AI Dating Coach</div>
            <h3 className="font-serif text-3xl text-[#1A1818] mt-1 leading-none">Ask anything.</h3>
            <p className="text-xs text-[#737373] mt-2 italic">about your chat with {otherName}</p>
          </div>
          <button data-testid="coach-close" onClick={onClose}
            className="w-9 h-9 rounded-full border border-[#E5DFD3] hover:bg-[#F0EBE1] flex items-center justify-center">
            <X size={16} />
          </button>
        </header>

        {!isQuietPlus ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-10" data-testid="coach-gated">
            <div className="w-14 h-14 rounded-full bg-[#F3E8DD] border border-[#D4A373]/40 flex items-center justify-center mb-5">
              <Lock size={20} className="text-[#913831]" />
            </div>
            <h4 className="font-serif text-3xl text-[#1A1818]">Coaching is part of Quiet+.</h4>
            <p className="text-sm text-[#737373] mt-3 max-w-xs">
              Get on-demand, conversation-aware advice from Claude — what to say next, when to ask them out, how to read a stall.
            </p>
            <Link to="/billing" data-testid="coach-upgrade-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-6 py-3 font-medium">
              <Sparkles size={14} /> Upgrade to Quiet+
            </Link>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="coach-thread">
              {history.length === 0 && (
                <div className="text-center text-[#737373] mt-2">
                  <Sparkles size={20} strokeWidth={1.5} className="mx-auto text-[#913831]" />
                  <p className="text-sm mt-2 max-w-xs mx-auto font-serif italic">
                    Try one of the prompts below, or write your own.
                  </p>
                </div>
              )}
              {history.map((h, i) => (
                <div key={`${i}-${h.q.slice(0, 20)}`} className="space-y-2" data-testid={`coach-exchange-${i}`}>
                  <div className="flex justify-end">
                    <div className="max-w-[85%] bg-[#1A1818] text-white rounded-2xl rounded-tr-md px-4 py-2.5 text-sm">
                      {h.q}
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="max-w-[90%] bg-white border border-[#E5DFD3] rounded-2xl rounded-tl-md px-4 py-3 text-sm text-[#1A1818] font-serif leading-relaxed" data-testid={`coach-answer-${i}`}>
                      {h.a == null
                        ? <span className="ai-shimmer font-medium not-italic">Thinking…</span>
                        : h.a}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-[#E5DFD3] bg-white/60" data-testid="coach-composer">
              {history.length === 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {SUGGESTED_PROMPTS.map((p, i) => (
                    <button key={i} data-testid={`coach-suggest-${i}`} onClick={() => ask(p)}
                      className="text-xs px-3 py-1.5 rounded-full bg-[#F3E8DD] border border-[#D4A373]/40 hover:bg-[#EADBC8] text-[#1A1818]">
                      {p}
                    </button>
                  ))}
                </div>
              )}
              <form className="flex items-center gap-2"
                    onSubmit={(e) => { e.preventDefault(); ask(); }}>
                <input
                  data-testid="coach-input"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Ask Claude anything…"
                  className="flex-1 bg-white border border-[#E5DFD3] rounded-full px-5 py-3 text-sm focus:outline-none focus:border-[#913831]"
                />
                <button type="submit" data-testid="coach-send" disabled={busy || !question.trim()}
                  className="w-11 h-11 rounded-full bg-[#913831] hover:bg-[#7A2E28] disabled:opacity-50 text-white flex items-center justify-center">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </form>
            </div>
          </>
        )}
      </motion.aside>
    </div>
  );
}
